// US-077 — Push-to-Leads inline bar.
//
// Sticky strip between thread + footer. Mirrors `ls-inbox.jsx:809-883`.
// Three states:
//   - default (qualified-lead CTA),
//   - manual-pushed (✅ confirmation strip + `Voir →` link),
//   - auto-pushed (✦ confirmation copy).
//
// `Voir →` navigates to the Leads tab — fixes the proto bug
// (`_recon/01-inbox.md` Open Q 3) where it pointed at Accounts.

import { useT } from '@barel/i18n';
import { Button } from '@barel/ui';

import type { ConversationView } from './types';

export interface PushToLeadsBarProps {
  conversation: Pick<ConversationView, 'id' | 'leadPushed' | 'leadPushedAuto'>;
  onPush: () => Promise<void> | void;
  onViewLeads?: () => void;
  busy?: boolean;
}

export function PushToLeadsBar({
  conversation,
  onPush,
  onViewLeads,
  busy = false,
}: PushToLeadsBarProps) {
  const t = useT('inbox');
  if (conversation.leadPushed) {
    const auto = !!conversation.leadPushedAuto;
    return (
      <div
        data-ls-component="push-to-leads-bar"
        data-state="pushed"
        style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--border-subtle)',
          background: 'var(--success-bg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2_5)',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          flexShrink: 0,
        }}
      >
        <span aria-hidden style={{ fontSize: 14 }}>
          {auto ? '✦' : '✅'}
        </span>
        <span style={{ flex: 1 }}>
          {auto ? t('thread.pushToLeads.pushedAuto') : t('thread.pushToLeads.pushedManual')}
        </span>
        {onViewLeads ? (
          <Button variant="tertiary" size="sm" onClick={onViewLeads} data-ls-action="view-leads">
            {t('thread.pushToLeads.viewLink')}
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div
      data-ls-component="push-to-leads-bar"
      data-state="cta"
      style={{
        padding: '9px 16px',
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2_5)',
        flexShrink: 0,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 7,
          background: 'var(--success-bg)',
          border: '1px solid var(--success-border)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
        }}
      >
        ✅
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            lineHeight: 1.3,
          }}
        >
          {t('thread.pushToLeads.qualifiedTitle')}{' '}
          <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t('thread.pushToLeads.qualifiedHint')}
          </span>
        </div>
      </div>
      <Button
        variant="success"
        size="sm"
        onClick={onPush}
        disabled={busy}
        data-ls-action="push-to-leads"
      >
        {t('thread.pushToLeads.cta')}
      </Button>
    </div>
  );
}
